import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import PortalLayout from "@/components/ventrix/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatMoney } from "@/components/ventrix/atoms";
import { ChevronLeft, ChevronRight, CheckCircle2, Plane, Ship, Truck, Package, Search } from "lucide-react";

const STEPS = ["Parties", "Cargo", "Service", "Documents", "Review"];
const MODE_META = { air: { Icon: Plane, l: "Air" }, sea: { Icon: Ship, l: "Sea" }, road: { Icon: Truck, l: "Road" } };

const blankParty = () => ({
  name: "", company: "", address_line1: "", city: "", country: "", postal_code: "", contact_email: "", contact_phone: "",
});
const blankItem = () => ({
  description: "", pieces: 1, weight_kg: 0, length_cm: 0, width_cm: 0, height_cm: 0,
  hs_code: "", declared_value: 0, currency: "USD", dangerous_goods: false,
});

export default function Book() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [shipper, setShipper] = useState(blankParty());
  const [consignee, setConsignee] = useState(blankParty());
  const [items, setItems] = useState([blankItem()]);
  const [mode, setMode] = useState("air");
  const [service, setService] = useState("standard");
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [incoterms, setIncoterms] = useState("DAP");
  const [notes, setNotes] = useState("");
  const [quote, setQuote] = useState(null);
  const [customPrice, setCustomPrice] = useState("");   // staff override of the quoted total
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [accountId, setAccountId] = useState("");   // customer to book for; "" = internal
  const [customers, setCustomers] = useState([]);
  const [custQuery, setCustQuery] = useState("");   // search box for the customer picker
  const [customerEmail, setCustomerEmail] = useState("");  // where shipment updates are emailed
  const [quoteRequestId, setQuoteRequestId] = useState(null);  // set when booking from a quote
  const loc = useLocation();
  useEffect(() => { api.get("/v1/customers").then(({ data }) => setCustomers(data.items || [])).catch(() => {}); }, []);

  // Prefill from a converted quote request (everything stays editable).
  useEffect(() => {
    const q = loc.state?.fromQuote;
    if (!q) return;
    const inp = q.input || {};
    setOrigin(inp.origin || "");
    setDestination(inp.destination || "");
    if (["air", "sea", "road"].includes(inp.mode)) setMode(inp.mode);
    setItems([{
      ...blankItem(),
      pieces: Number(inp.pieces) || 1,
      weight_kg: Number(inp.weight_kg) || 0,
      length_cm: Number(inp.length_cm) || 0,
      width_cm: Number(inp.width_cm) || 0,
      height_cm: Number(inp.height_cm) || 0,
      dangerous_goods: inp.cargo_type === "dangerous",
    }]);
    if (q.email) setCustomerEmail(q.email);
    const price = q.sent_total != null ? q.sent_total : (q.computed || {}).total;
    if (price != null) setCustomPrice(String(price));
    setQuoteRequestId(q.id || null);
  }, [loc.state]); // eslint-disable-line

  const selectedCustomer = useMemo(
    () => customers.find((c) => c.account_id === accountId) || null,
    [customers, accountId]
  );
  const customerMatches = useMemo(() => {
    const q = custQuery.trim().toLowerCase();
    const pool = q
      ? customers.filter((c) => `${c.name || ""} ${c.company || ""} ${c.email || ""}`.toLowerCase().includes(q))
      : customers;
    return pool.slice(0, 8);
  }, [customers, custQuery]);

  const totalWeight = useMemo(
    () => items.reduce((s, i) => s + (Number(i.weight_kg) || 0) * (Number(i.pieces) || 1), 0),
    [items]
  );

  const getQuote = async () => {
    if (!origin || !destination || items.length === 0) return;
    try {
      const it = items[0];
      const { data } = await api.post("/v1/quote", {
        origin, destination, mode,
        weight_kg: Number(it.weight_kg) || 0,
        length_cm: Number(it.length_cm) || 0, width_cm: Number(it.width_cm) || 0, height_cm: Number(it.height_cm) || 0,
        pieces: items.reduce((s,i)=>s+Number(i.pieces||1), 0),
        cargo_type: it.dangerous_goods ? "dangerous" : "general",
        sea_container: mode === "sea" ? "lcl" : null,
      });
      setQuote(data.quote);
      // Prefill the editable price with the system estimate; staff can override it.
      if (customPrice === "") setCustomPrice(String(data.quote.total));
    } catch (e) { toast.error("Could not compute quote"); }
  };

  const next = async () => {
    if (step === 1) { await getQuote(); }
    setStep(Math.min(STEPS.length - 1, step + 1));
  };
  const back = () => setStep(Math.max(0, step - 1));

  const submit = async () => {
    setBusy(true);
    try {
      const cleanParty = (p) => Object.fromEntries(Object.entries(p).map(([k, v]) => [k, v === "" ? null : v]));
      const payload = {
        mode, service_level: service, origin, destination, incoterms, notes,
        shipper: cleanParty(shipper), consignee: cleanParty(consignee),
        items: items.map(i => ({ ...i, pieces: Number(i.pieces) || 1,
          weight_kg: Number(i.weight_kg) || 0, length_cm: Number(i.length_cm) || 0,
          width_cm: Number(i.width_cm) || 0, height_cm: Number(i.height_cm) || 0,
          declared_value: Number(i.declared_value) || 0,
          hs_code: i.hs_code || null })),
        quoted_amount: customPrice !== "" ? Number(customPrice) : quote?.total,
        currency: quote?.currency || "USD",
        account_id: accountId || null,
        customer_email: customerEmail.trim() || null,
        quote_request_id: quoteRequestId,
      };
      const { data } = await api.post("/v1/bookings", payload);
      setDone(data.shipment);
      toast.success(`Booking confirmed: ${data.shipment.booking_ref}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <PortalLayout section="ops">
        <div className="vtx-card p-10 max-w-2xl mx-auto text-center">
          <CheckCircle2 size={48} className="mx-auto text-[#166534]" />
          <h2 className="mt-4 text-3xl font-black text-[#0E2F76] tracking-tighter">Booking created</h2>
          <div className="mt-2 text-sm text-[#475569]">The shipment is awaiting approval before tracking begins.</div>
          <div className="mt-6 grid grid-cols-2 gap-3 text-left">
            <div><div className="vtx-eyebrow">Booking ref</div><div className="font-mono font-bold text-[#0E2F76] mt-1">{done.booking_ref}</div></div>
            <div><div className="vtx-eyebrow">Tracking number</div><div className="font-mono font-bold text-[#0E2F76] mt-1">{done.tracking_number}</div></div>
          </div>
          <div className="mt-8 flex gap-3 justify-center">
            <button className="vtx-btn-primary" onClick={()=>nav(`/ops/shipments/${done.id}`)} data-testid="goto-shipment">Open shipment</button>
            <button className="vtx-btn-ghost" onClick={()=>{ setDone(null); setStep(0); }} data-testid="new-booking-again">Book another</button>
          </div>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout section="ops">
      <div className="max-w-4xl">
        <div className="vtx-eyebrow">/ Ops · New booking</div>
        <h1 className="mt-2 text-3xl font-black text-[#0E2F76] tracking-tighter">Create a booking</h1>

        <div className="mt-4 vtx-card p-4" data-testid="book-customer">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span className="vtx-eyebrow">Booking for</span>
            {selectedCustomer ? (
              <div className="flex items-center gap-2" data-testid="book-customer-selected">
                <span className="vtx-chip" style={{ backgroundColor: "#EEF3FB", color: "#0E2F76" }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "#0E2F76" }} />
                  {selectedCustomer.name}{selectedCustomer.company ? ` · ${selectedCustomer.company}` : ""} ({selectedCustomer.email})
                </span>
                <button type="button" className="text-xs text-[#d41f26] font-bold hover:underline"
                  onClick={() => { setAccountId(""); setCustQuery(""); }} data-testid="book-customer-change">Change</button>
              </div>
            ) : (
              <span className="text-xs text-[#475569]">Internal / unassigned — search below to assign a customer.</span>
            )}
          </div>

          {!selectedCustomer && (
            <div className="relative mt-3 max-w-md">
              <div className="relative">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#475569]" />
                <input className="vtx-input pl-9" placeholder="Search customers by name, company or email…"
                  value={custQuery} onChange={(e) => setCustQuery(e.target.value)} data-testid="book-customer-search" autoComplete="off" />
              </div>
              {custQuery.trim() && (
                <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-[#A9C0E0]/60 shadow-lg max-h-64 overflow-auto" style={{ borderRadius: 2 }} data-testid="book-customer-results">
                  {customerMatches.length === 0 ? (
                    <div className="px-3 py-3 text-sm text-[#475569]">No matching customers.</div>
                  ) : customerMatches.map((c) => (
                    <button type="button" key={c.id} onClick={() => { setAccountId(c.account_id); setCustQuery(""); setCustomerEmail(c.email || ""); }}
                      data-testid={`book-customer-opt-${c.account_id}`}
                      className="w-full text-left px-3 py-2 hover:bg-[#F4FEFF] border-b border-[#A9C0E0]/20 last:border-0">
                      <div className="text-sm font-semibold text-[#0E2F76]">{c.name}{c.company ? ` · ${c.company}` : ""}</div>
                      <div className="text-xs text-[#475569] font-mono">{c.email}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="mt-3 max-w-md">
            <label className="block">
              <span className="vtx-eyebrow block mb-1.5">Customer email for updates</span>
              <input type="email" className="vtx-input" placeholder="customer@company.com"
                value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} data-testid="book-customer-email" />
            </label>
            <div className="mt-1 text-[11px] text-[#475569]">Shipment update emails are sent here. Prefilled when you pick a customer; enter it for a walk-in or internal booking so the customer still gets updates.</div>
          </div>
        </div>

        <div className="mt-6 flex items-center gap-2 flex-wrap">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-7 h-7 grid place-items-center text-xs font-bold ${i <= step ? "bg-[#0E2F76] text-white" : "bg-white text-[#475569] border border-[#A9C0E0]"}`} style={{ borderRadius: 2 }}>
                {i < step ? <CheckCircle2 size={14}/> : i + 1}
              </div>
              <div className={`text-xs font-semibold uppercase tracking-wider ${i === step ? "text-[#0E2F76]" : "text-[#475569]"}`}>{s}</div>
              {i < STEPS.length - 1 && <div className="w-6 h-px bg-[#A9C0E0]/60" />}
            </div>
          ))}
        </div>

        <div className="mt-6 vtx-card p-6">
          {step === 0 && (
            <div className="grid md:grid-cols-2 gap-8">
              <PartyForm title="Shipper" data={shipper} setData={setShipper} testPrefix="shipper" />
              <PartyForm title="Consignee" data={consignee} setData={setConsignee} testPrefix="consignee" />
            </div>
          )}
          {step === 1 && (
            <div>
              <div className="vtx-eyebrow">Route</div>
              <div className="grid md:grid-cols-2 gap-4 mt-3">
                <label><span className="vtx-eyebrow block mb-1.5">Origin</span><input className="vtx-input" value={origin} onChange={(e)=>setOrigin(e.target.value)} placeholder="Mombasa, KE" data-testid="book-origin" required /></label>
                <label><span className="vtx-eyebrow block mb-1.5">Destination</span><input className="vtx-input" value={destination} onChange={(e)=>setDestination(e.target.value)} placeholder="Rotterdam, NL" data-testid="book-destination" required /></label>
              </div>
              <div className="vtx-eyebrow mt-8">Cargo items</div>
              <div className="mt-3 space-y-4">
                {items.map((it, i) => (
                  <div key={i} className="border border-[#A9C0E0]/60 p-4 relative" style={{borderRadius:2}}>
                    <div className="grid md:grid-cols-4 gap-3">
                      <label className="md:col-span-2"><span className="vtx-eyebrow block mb-1.5">Description</span><input className="vtx-input" value={it.description} onChange={(e)=>setItems(items.map((x,ix)=>ix===i?{...x, description: e.target.value}:x))} data-testid={`cargo-desc-${i}`} required /></label>
                      <label><span className="vtx-eyebrow block mb-1.5">Pieces</span><input type="number" min="1" className="vtx-input" value={it.pieces} onChange={(e)=>setItems(items.map((x,ix)=>ix===i?{...x, pieces: e.target.value}:x))} data-testid={`cargo-pieces-${i}`} /></label>
                      <label><span className="vtx-eyebrow block mb-1.5">Weight/pc (kg)</span><input type="number" step="0.1" className="vtx-input" value={it.weight_kg} onChange={(e)=>setItems(items.map((x,ix)=>ix===i?{...x, weight_kg: e.target.value}:x))} data-testid={`cargo-weight-${i}`} required /></label>
                      <label><span className="vtx-eyebrow block mb-1.5">L (cm)</span><input type="number" className="vtx-input" value={it.length_cm} onChange={(e)=>setItems(items.map((x,ix)=>ix===i?{...x, length_cm: e.target.value}:x))} /></label>
                      <label><span className="vtx-eyebrow block mb-1.5">W (cm)</span><input type="number" className="vtx-input" value={it.width_cm} onChange={(e)=>setItems(items.map((x,ix)=>ix===i?{...x, width_cm: e.target.value}:x))} /></label>
                      <label><span className="vtx-eyebrow block mb-1.5">H (cm)</span><input type="number" className="vtx-input" value={it.height_cm} onChange={(e)=>setItems(items.map((x,ix)=>ix===i?{...x, height_cm: e.target.value}:x))} /></label>
                      <label><span className="vtx-eyebrow block mb-1.5">HS code</span><input className="vtx-input font-mono" value={it.hs_code} onChange={(e)=>setItems(items.map((x,ix)=>ix===i?{...x, hs_code: e.target.value}:x))} /></label>
                      <label><span className="vtx-eyebrow block mb-1.5">Declared value</span><input type="number" step="0.01" className="vtx-input" value={it.declared_value} onChange={(e)=>setItems(items.map((x,ix)=>ix===i?{...x, declared_value: e.target.value}:x))} /></label>
                      <label className="flex items-end gap-2 mb-2 md:col-span-2">
                        <input type="checkbox" checked={it.dangerous_goods} onChange={(e)=>setItems(items.map((x,ix)=>ix===i?{...x, dangerous_goods: e.target.checked}:x))} data-testid={`cargo-dg-${i}`} />
                        <span className="text-xs font-semibold uppercase tracking-wider text-[#d41f26]">Dangerous goods</span>
                      </label>
                    </div>
                    {items.length > 1 && (
                      <button className="absolute top-2 right-3 text-xs text-[#d41f26] font-bold" onClick={()=>setItems(items.filter((_,ix)=>ix!==i))} data-testid={`remove-item-${i}`}>Remove</button>
                    )}
                  </div>
                ))}
                <button type="button" className="text-sm text-[#0E2F76] font-semibold hover:text-[#d41f26] inline-flex items-center gap-1" onClick={()=>setItems([...items, blankItem()])} data-testid="add-cargo-item">+ Add another item</button>
              </div>
              <div className="mt-4 text-xs text-[#475569]">Total weight: <span className="font-mono font-bold text-[#0E2F76]">{totalWeight.toFixed(2)} kg</span></div>
            </div>
          )}
          {step === 2 && (
            <div>
              <div className="vtx-eyebrow">Mode</div>
              <div className="mt-3 grid grid-cols-3 gap-3">
                {Object.entries(MODE_META).map(([v, m]) => (
                  <button key={v} onClick={()=>setMode(v)} data-testid={`book-mode-${v}`}
                    className={`p-4 border transition-colors flex flex-col items-center gap-2 ${mode === v ? "bg-[#0E2F76] text-white border-[#0E2F76]" : "bg-white text-[#0E2F76] border-[#A9C0E0]/60 hover:border-[#0E2F76]"}`} style={{borderRadius:2}}>
                    <m.Icon size={22} strokeWidth={2.2} />
                    <span className="text-xs font-bold uppercase tracking-wider">{m.l}</span>
                  </button>
                ))}
              </div>
              <div className="vtx-eyebrow mt-6">Service level</div>
              <div className="mt-3 grid grid-cols-3 gap-3">
                {["economy","standard","express"].map(s => (
                  <button key={s} onClick={()=>setService(s)} data-testid={`book-service-${s}`}
                    className={`p-3 border text-sm font-semibold transition-colors ${service === s ? "bg-[#0E2F76] text-white border-[#0E2F76]" : "bg-white text-[#0E2F76] border-[#A9C0E0]/60 hover:border-[#0E2F76]"}`} style={{borderRadius:2}}>
                    {s.charAt(0).toUpperCase()+s.slice(1)}
                  </button>
                ))}
              </div>
              <div className="vtx-eyebrow mt-6">Incoterms</div>
              <select className="vtx-input mt-3 max-w-xs" value={incoterms} onChange={(e)=>setIncoterms(e.target.value)} data-testid="book-incoterms">
                {["EXW","FCA","FOB","CIF","CIP","DAP","DDP"].map(x => <option key={x}>{x}</option>)}
              </select>
            </div>
          )}
          {step === 3 && (
            <div>
              <div className="vtx-eyebrow">Notes for ops</div>
              <textarea rows={5} className="vtx-input mt-3" placeholder="Special handling, dock hours, hazmat notes…" value={notes} onChange={(e)=>setNotes(e.target.value)} data-testid="book-notes" />
              <div className="mt-4 vtx-card p-4 bg-[#F4FEFF]">
                <div className="text-xs text-[#475569]">Document uploads (bill of lading, commercial invoice, packing list, customs docs) will attach to the shipment after booking. <span className="font-semibold">Coming soon.</span></div>
              </div>
            </div>
          )}
          {step === 4 && (
            <div className="grid md:grid-cols-3 gap-6">
              <div className="md:col-span-2 space-y-4">
                <ReviewBlock title="Route" rows={[
                  ["Mode", mode.toUpperCase()], ["Service", service],
                  ["Origin", origin], ["Destination", destination], ["Incoterms", incoterms],
                ]} />
                <ReviewBlock title="Shipper" rows={[
                  ["Name", shipper.name], ["Company", shipper.company || "—"],
                  ["Address", `${shipper.address_line1}, ${shipper.city}, ${shipper.country}`],
                ]} />
                <ReviewBlock title="Consignee" rows={[
                  ["Name", consignee.name], ["Company", consignee.company || "—"],
                  ["Address", `${consignee.address_line1}, ${consignee.city}, ${consignee.country}`],
                ]} />
                <ReviewBlock title="Cargo" rows={items.map((it, i) => [`Item ${i+1}`,
                  `${it.description} · ${it.pieces} × ${it.weight_kg}kg · ${it.length_cm}×${it.width_cm}×${it.height_cm}${it.dangerous_goods ? " · DG" : ""}`])} />
              </div>
              <div className="vtx-card p-5 bg-[#0E2F76] text-white h-fit sticky top-24">
                <div className="vtx-eyebrow text-[#A9C0E0]">Quoted total</div>
                {quote ? (
                  <>
                    <div className="mt-3 text-4xl font-black tracking-tighter">{formatMoney(Number(customPrice) || quote.total, quote.currency)}</div>
                    <div className="mt-1 text-xs text-[#A9C0E0]">Chargeable {quote.chargeable_weight_kg} kg · ETA {quote.eta_days}d</div>
                    <div className="mt-4 text-xs text-[#A9C0E0] space-y-1">
                      <div className="flex justify-between"><span>Base</span><span className="font-mono">{formatMoney(quote.base, quote.currency)}</span></div>
                      <div className="flex justify-between"><span>Fuel</span><span className="font-mono">{formatMoney(quote.surcharges.fuel, quote.currency)}</span></div>
                      <div className="flex justify-between"><span>Security</span><span className="font-mono">{formatMoney(quote.surcharges.security, quote.currency)}</span></div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-white/15">
                      <div className="text-[11px] uppercase tracking-wider text-[#A9C0E0] mb-1.5">Custom price ({quote.currency})</div>
                      <input type="number" min="0" step="0.01" value={customPrice} onChange={(e)=>setCustomPrice(e.target.value)}
                        className="w-full px-3 py-2 text-[#0E2F76] font-bold" style={{ borderRadius: 2 }} data-testid="book-custom-price" />
                      <div className="mt-1.5 text-[11px] text-[#A9C0E0]">Overrides the system estimate for this booking. Leave as-is to charge the quoted total.</div>
                    </div>
                  </>
                ) : (
                  <div className="mt-3 text-sm text-[#A9C0E0]">Quote will be recomputed on submit.</div>
                )}
                <button className="vtx-btn-danger w-full mt-6" onClick={submit} disabled={busy} data-testid="book-confirm">
                  {busy ? "Booking…" : "Confirm booking"}
                </button>
              </div>
            </div>
          )}

          <div className="mt-8 flex justify-between">
            <button className="vtx-btn-ghost inline-flex items-center gap-2" onClick={back} disabled={step===0} data-testid="wizard-back"><ChevronLeft size={14}/> Back</button>
            {step < STEPS.length - 1 && <button className="vtx-btn-primary inline-flex items-center gap-2" onClick={next} data-testid="wizard-next">Next <ChevronRight size={14}/></button>}
          </div>
        </div>
      </div>
    </PortalLayout>
  );
}

function PartyForm({ title, data, setData, testPrefix }) {
  const set = (k) => (e) => setData({ ...data, [k]: e.target.value });
  return (
    <div>
      <div className="vtx-eyebrow">{title}</div>
      <div className="mt-3 space-y-3">
        <input className="vtx-input" placeholder="Name" value={data.name} onChange={set("name")} data-testid={`${testPrefix}-name`} required />
        <input className="vtx-input" placeholder="Company (optional)" value={data.company} onChange={set("company")} data-testid={`${testPrefix}-company`} />
        <input className="vtx-input" placeholder="Address line 1" value={data.address_line1} onChange={set("address_line1")} data-testid={`${testPrefix}-address`} required />
        <div className="grid grid-cols-2 gap-3">
          <input className="vtx-input" placeholder="City" value={data.city} onChange={set("city")} data-testid={`${testPrefix}-city`} required />
          <input className="vtx-input" placeholder="Country" value={data.country} onChange={set("country")} data-testid={`${testPrefix}-country`} required />
        </div>
        <input className="vtx-input" placeholder="Postal code" value={data.postal_code} onChange={set("postal_code")} />
        <div className="grid grid-cols-2 gap-3">
          <input className="vtx-input" placeholder="Contact email" type="email" value={data.contact_email} onChange={set("contact_email")} />
          <input className="vtx-input" placeholder="Contact phone" value={data.contact_phone} onChange={set("contact_phone")} />
        </div>
      </div>
    </div>
  );
}

function ReviewBlock({ title, rows }) {
  return (
    <div className="vtx-card p-5">
      <div className="vtx-eyebrow">{title}</div>
      <div className="mt-3 grid grid-cols-1 gap-2">
        {rows.map(([k,v]) => (
          <div key={k} className="flex justify-between text-sm border-b border-[#A9C0E0]/30 pb-1.5">
            <span className="text-[#475569]">{k}</span><span className="font-semibold text-[#0f172a]">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
